/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewVariant.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MultipleVariants from "./MultipleVariants";
import AddButtonV2 from "../common/button/AddButtonV2";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSelector } from "react-redux";
import CustomToolTip from "../../infoButton/CustomToolTip";
import { archiveReasons } from "../../../config/appConstants";

function ViewVariant({
  expanded,
  setExpanded,
  variantDetailsSelected,
  expandedIndex,
  setExpandedIndex,
  handleVariantToggle,
  handleAddVariant,
  logs,
  loading,
  setExpandVCLogs,
  expandVCLogs,
  vehicleStatusData,
  logsCount,
  handleEditClick,
  rowsPerPageVCLogCount,
  pageVCLogCount,
  handlePageChangeLogsVC,
  isRecreatedProject,
  onRecreateVariantClick,
  onCopyVariant,
  projectStatus,
  projectArchiveStatus,
  archive_status,
  archive_reason,
}) {
  const reduxProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );

  const effectiveArchiveStatus =
    projectArchiveStatus ||
    archive_status ||
    reduxProjectDetails?.archive_status;

  const isProjectActive =
    String(effectiveArchiveStatus)?.toUpperCase() === "ACTIVE";

  const effectiveArchiveReasons = (() => {
    if (archive_reason) {
      return Array.isArray(archive_reason) ? archive_reason : [archive_reason];
    }
    if (reduxProjectDetails?.archive_reason) {
      return Array.isArray(reduxProjectDetails.archive_reason)
        ? reduxProjectDetails.archive_reason
        : [reduxProjectDetails.archive_reason];
    }
    if (Array.isArray(variantDetailsSelected) && variantDetailsSelected.length > 0) {
      const allVariantReasons = variantDetailsSelected.flatMap((v) => {
        if (Array.isArray(v?.archive_reason)) {
          return v.archive_reason;
        }
        if (v?.archive_reason) {
          return [v.archive_reason];
        }
        return [];
      });
      if (allVariantReasons.length > 0) {
        return Array.from(new Set(allVariantReasons));
      }
    }
    return [];
  })();

  const hasMissingArchiveReasons =
    Array.isArray(effectiveArchiveReasons) &&
    effectiveArchiveReasons.some(
      (r) => !archiveReasons?.variant_exceptionToActive?.includes(r),
    );

  const missingArchiveReasons = hasMissingArchiveReasons
    ? effectiveArchiveReasons.filter(
        (r) => !archiveReasons?.variant_exceptionToActive?.includes(r),
      )
    : [];

  const tooltipTitle =
    missingArchiveReasons.length > 0
      ? `Missing ${missingArchiveReasons.join(", ")}`
      : "";

  const handleAccordionToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Accordion
        expanded={expanded}
        sx={{
          boxShadow: 3,
          borderRadius: 0,
          backgroundColor: "#f9f9f9",
          "&:before": { display: "none" },
          p: 0,
          "& .MuiAccordionDetails-root": {
            p: 0,
          },
        }}
        onChange={handleAccordionToggle}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
          sx={{
            backgroundColor: "black",
            color: "white",
            minHeight: "48px !important",
            "& .MuiAccordionSummary-content": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              p: 0,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontFamily: "Manrope, sans-serif" }}
          >
            Variant Configuration
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {hasMissingArchiveReasons && (
              <Box
                sx={{ display: "flex", alignItems: "center" }}
                onClick={(e) => e.stopPropagation()}
              >
                <CustomToolTip
                  type="component"
                  title={tooltipTitle}
                >
                  <ErrorOutlineIcon
                    sx={{ fontSize: "18px", color: "red" }}
                  />
                </CustomToolTip>
              </Box>
            )}
            {isRecreatedProject && (
              <Box
                sx={{ mb: -1, cursor: "pointer" }}
                onClick={onRecreateVariantClick}
              >
                <RefreshIcon />
              </Box>
            )}
            {isProjectActive && <AddButtonV2 handleOnClick={handleAddVariant} />}
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            backgroundColor: "white",
            maxHeight: 600,
          }}
        >
          {variantDetailsSelected?.length > 0 ? (
            variantDetailsSelected?.map((variant, index) => (
              <MultipleVariants
                handlePageChangeLogsVC={handlePageChangeLogsVC}
                logsCount={logsCount}
                handleEditClick={handleEditClick}
                setExpandVCLogs={setExpandVCLogs}
                expandVCLogs={expandVCLogs}
                loading={loading}
                logs={logs}
                key={variant?.id || crypto.randomUUID()}
                variant={variant}
                expanded={expandedIndex === index}
                onToggle={() => handleVariantToggle(index, variant)}
                vehicleStatusData={vehicleStatusData}
                pageVCLogCount={pageVCLogCount}
                rowsPerPageVCLogCount={rowsPerPageVCLogCount}
                onCopyVariant={onCopyVariant}
                projectStatus={projectStatus}
              />
            ))
          ) : (
            <Typography>No Variants Found</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

ViewVariant.propTypes = {
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func,
  variantDetailsSelected: PropTypes.array,
  expandedIndex: PropTypes.number,
  setExpandedIndex: PropTypes.func,
  handleVariantToggle: PropTypes.func,
  handleAddVariant: PropTypes.func,
  logs: PropTypes.array,
  loading: PropTypes.bool,
  setExpandVCLogs: PropTypes.func,
  expandVCLogs: PropTypes.any,
  vehicleStatusData: PropTypes.any,
  logsCount: PropTypes.number,
  handleEditClick: PropTypes.func,
  rowsPerPageVCLogCount: PropTypes.number,
  pageVCLogCount: PropTypes.number,
  handlePageChangeLogsVC: PropTypes.func,
  isRecreatedProject: PropTypes.bool,
  onRecreateVariantClick: PropTypes.func,
  onCopyVariant: PropTypes.func,
  projectStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectArchiveStatus: PropTypes.string,
  archive_status: PropTypes.string,
  archive_reason: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

export default ViewVariant;
